import {
  AnyPgColumn,
  bigint,
  boolean,
  check,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgTable,
  uuid,
  varchar,
  text,
  jsonb,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import type { DescriptionContent } from '@trashlab/api/src/shared/data/description-content';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { distance_fee } from '@trashlab/api/src/domains/pricing/schemas/distance-fee';
import { fuel_surcharge } from '@trashlab/api/src/domains/pricing/schemas/fuel-surcharge';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';
import { decimal } from '@trashlab/api/src/domains/core/schemas/base-template';
import { Decimal } from 'decimal.js';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';

/**
 * Enum defining how surcharges are applied to prices.
 * - FLAT: Fixed amount surcharge
 * - PERCENTAGE: Percentage-based surcharge
 */
export const surcharge_type = pgEnum('surcharge_type', ['FLAT', 'PERCENTAGE']);

/**
 * Enum defining how recurring charges are applied relative to the service date.
 * - FORWARD: Charges applied for future periods
 * - BACKWARD: Charges applied for past periods
 */
export const recurring_forward_or_backward = pgEnum(
  'recurring_forward_or_backward',
  ['FORWARD', 'BACKWARD'],
);

/**
 * Enum defining driver visibility for extra fees.
 * - ALL: Visible to all drivers
 * - NONE: Hidden from all drivers
 * - SOME: Visible to specific drivers only
 */
export const driver_visibility = pgEnum('driver_visibility', [
  'ALL',
  'NONE',
  'SOME',
]);

/** Unit used to calculate the quantity for a price. */
export const price_unit_of_measure = pgEnum('price_unit_of_measure', [
  'SERVICE',
  'LIFT',
  'TON_OVERAGE',
]);

/** Canadian tax components explicitly selected for a price. NULL preserves legacy behavior. */
export const canadian_tax_applicability = pgEnum('canadian_tax_applicability', [
  'NONE',
  'GST',
  'PST',
  'GST_PST',
]);

export type CanadianTaxApplicability =
  (typeof canadian_tax_applicability)['enumValues'][number];

export type PriceUnitOfMeasure =
  (typeof price_unit_of_measure)['enumValues'][number];

/**
 * Enum defining events that trigger price charges.
 * - ON_ORDER_PAYMENT_POLICY: Charge based on order payment policy
 * - ON_DELIVERY_WO: Charge when delivery work order is created
 * - ON_REMOVAL_WO: Charge when removal work order is created
 * - ON_SERVICE_WO: Charge when service work order is created
 * - ON_SERVICE_WO_NON_RECURRING: Charge when service work order is created (only for non-recurring orders)
 * - ON_LIVELOAD_WO: Charge when live load work order is created
 * - ON_SWAP_WO: Charge when swap work order is created
 * - ON_DUMP_AND_RETURN_WO: Charge when dump and return work order is created
 * - ON_RESPOT_WO: Charge when respot work order is created
 * - ON_MIGRATE_WO: Charge when migrate work order is created
 * - RECURRING: Charge on a recurring schedule
 */
export const price_charge_trigger = pgEnum('price_charge_trigger', [
  'ON_ORDER_PAYMENT_POLICY',
  'ON_DELIVERY_WO',
  'ON_REMOVAL_WO',
  'ON_SERVICE_WO',
  'ON_SERVICE_WO_NON_RECURRING',
  'ON_LIVELOAD_WO',
  'ON_SWAP_WO',
  'ON_DUMP_AND_RETURN_WO',
  'ON_RESPOT_WO',
  'ON_MIGRATE_WO',
  'ON_EXCEPTION',
  'RECURRING',
]);

/**
 * Enum defining what the price calculation is based on.
 * - TOTAL_ORDER_AMOUNT: Based on total order value
 * - DUMPSTER: Based on dumpster fees
 * - PICKUP: Based on pickup fees
 * - DISTANCE_BASED_FEE: Based on distance traveled
 * - DELIVERY: Based on delivery fees
 * - ON_DEMAND_SERVICE: Based on on-demand service fees
 * - SURCHARGE: Based on other surcharges
 * - RECURRING: Based on recurring charges
 * - ADDITIONAL_DAYS: Based on extra rental days
 * - DRY_RUN: Based on dry run fees
 * - ADDITIONAL_TONS: Based on excess tonnage
 * - MIN_TONNAGE_FEE: Based on minimum tonnage requirements
 * - RESPOT: Based on the respot fee
 */
export const price_calculation_base = pgEnum('price_calculation_base', [
  'TOTAL_ORDER_AMOUNT',
  'DUMPSTER',
  'PICKUP',
  'DISTANCE_BASED_FEE',
  'DELIVERY',
  'ON_DEMAND_SERVICE',
  'SURCHARGE',
  'RECURRING',
  'ADDITIONAL_DAYS',
  'DRY_RUN',
  'ADDITIONAL_TONS',
  'MIN_TONNAGE_FEE',
  'RESPOT',
]);

/** Type for price charge trigger enum values */
export type PriceChargeTrigger =
  (typeof price_charge_trigger)['enumValues'][number];

/** Type for price calculation base enum values */
export type PriceCalculationBase =
  (typeof price_calculation_base)['enumValues'][number];

/**
 * Represents a pricing configuration for services and products.
 * Prices can be flat fees, percentages, or combinations with various triggers and calculation bases.
 * Supports complex pricing models including surcharges, recurring fees, and distance-based pricing.
 */
export const price = pgTable(
  'price',
  {
    ...defaultFields.fieldsWithTenantId(),
    // Durable opt-out written only by an explicit removal in the new editor.
    billingDisabled: boolean('billingDisabled').notNull().default(false),
    populateWhenZero: boolean('populateWhenZero').notNull().default(false),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
    /** Type of price (e.g., base, surcharge, recurring) */
    priceType: varchar('priceType', { length: 255 }).notNull(),
    /** Fixed fee amount in cents */
    chargeFee: bigint('chargeFee', { mode: 'number' }).default(0).notNull(),
    /** Percentage-based charge amount */
    chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 })
      .default(new Decimal(0))
      .notNull(),
    /** Reference to associated SKU */
    skuId: uuid('skuId').references((): AnyPgColumn => sku_v2.id),
    /** Name of the surcharge if applicable */
    surchargeName: varchar('surchargeName', { length: 255 }),
    /** Surcharge order for display purposes */
    surchargeOrder: integer('surchargeOrder'),
    /** Type of surcharge (flat or percentage) */
    surchargeType: surcharge_type('surchargeType'),
    /** Optional shared fuel surcharge definition */
    fuelSurchargeId: uuid('fuelSurchargeId'),
    /** Name of recurring charge */
    recurrenceName: varchar('recurrenceName', { length: 255 }),
    /** Rule defining recurring charge schedule */
    recurrenceRule: varchar('recurrenceRule', { length: 255 }),
    /**
     * Full revolver period total in cents (e.g. $150 every 90 days = 15000).
     * This is the source of truth for recurring additional-days billing.
     * Cards without it are skipped until they are backfilled.
     */
    recurringPeriodAmountCents: bigint('recurringPeriodAmountCents', {
      mode: 'number',
    }),
    /** Whether the price is subject to tax */
    taxable: boolean('taxable').default(false).notNull(),
    /** ID from legacy system if this record was migrated */
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    /** Reference to distance-based fee configuration */
    distanceFeeId: uuid('distanceFeeId'),
    /** QuickBooks item ID for integration */
    qbItemId: varchar('qbItemId', { length: 255 }),
    /** QuickBooks class ID for integration */
    qbClassId: varchar('qbClassId', { length: 255 }),
    /** Display name for the price */
    priceName: text('priceName'),
    /** When true, subscription items inherit the order quantity during subscription creation */
    inheritOrderQuantity: boolean('inheritOrderQuantity')
      .notNull()
      .default(false),
    defaultQuantity: decimal('defaultQuantity', { precision: 12, scale: 4 }),
    /** Optional fallback business line for charges created from this extra fee */
    defaultBusinessLineId: uuid('defaultBusinessLineId'),
    /** Minimum billable quantity when applying this price */
    minimumQuantity: decimal('minimumQuantity', { precision: 100, scale: 4 }),
  /** Minimum total price in cents to charge for this line */
  minimumPriceCents: bigint('minimumPriceCents', { mode: 'number' }),
    /** Whether to charge forward or backward for recurring prices */
    recurringForwardOrBackward: recurring_forward_or_backward(
      'recurringForwardOrBackward',
    ),
    /** Events that trigger this price charge */
    chargeTriggers: price_charge_trigger('chargeTriggers')
      .array()
      .notNull()
      .default(sql`ARRAY[]::price_charge_trigger[]`),
    exceptionTypeIds: uuid('exceptionTypeIds')
      .array()
      .notNull()
      .default(sql`ARRAY[]::uuid[]`),
    /** Bases for calculating this price */
    calculationBase: price_calculation_base('calculationBase')
      .array()
      .notNull()
      .default(sql`ARRAY[]::price_calculation_base[]`),
    /** Driver visibility for extra fees (determines which drivers can see this fee) */
    driverVisibility: driver_visibility('driverVisibility')
      .notNull()
      .default('ALL'),
    unitOfMeasure: price_unit_of_measure('unitOfMeasure')
      .notNull()
      .default('SERVICE'),
    /** Explicit Canadian component selection. NULL uses legacy taxable behavior. */
    canadianTaxApplicability: canadian_tax_applicability(
      'canadianTaxApplicability',
    ),
    excludeCredits: boolean('excludeCredits').default(false).notNull(),
    descriptionContent: jsonb('descriptionContent').$type<DescriptionContent>(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('price_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('price_tenantId_idx').on(table.tenantId),
      updatedAtTs_idx: index('price_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('price_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('price_versionNumber_idx').on(
        table.versionNumber,
      ),
      distanceFeeId_idx: index('price_distanceFeeId_idx').on(
        table.distanceFeeId,
      ),
      fuelSurchargeId_idx: index('price_fuelSurchargeId_idx').on(
        table.fuelSurchargeId,
      ),
      defaultBusinessLineId_idx: index('price_defaultBusinessLineId_idx').on(
        table.defaultBusinessLineId,
      ),
      skuId_idx: index('price_skuId_idx').on(table.skuId),
      // Composite indexes to optimize list-skus query performance
      // These support filtered joins where we filter by priceType and join on skuId
      priceType_skuId_idx: index('price_priceType_skuId_idx').on(
        table.priceType,
        table.skuId,
      ),
      activeFixedExtraFeeNormalizedPrefixIdx: index(
        'price_active_fixed_extra_fee_normalized_prefix_idx',
      )
        .on(
          table.tenantId,
          sql`lower(btrim(COALESCE(${table.priceName}, ''))) text_pattern_ops`,
          table.id,
        )
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.priceType} = 'EXTRA_FEE'`,
        ),
      // Composite index to optimize recurring charge joins that also check recurrenceRule
      skuId_priceType_recurrenceRule_idx: index(
        'price_skuId_priceType_recurrenceRule_idx',
      ).on(table.skuId, table.priceType, table.recurrenceRule),
      price_unit_of_measure_price_type_check: check(
        'price_unit_of_measure_price_type_check',
        sql`${table.unitOfMeasure}::text = 'SERVICE' OR (${table.unitOfMeasure}::text = 'LIFT' AND ${table.priceType} IN ('ON_DEMAND_SERVICE', 'SURCHARGE')) OR (${table.unitOfMeasure}::text = 'TON_OVERAGE' AND ${table.priceType} = 'SURCHARGE')`,
      ),

      //foreign keys
      price_distanceFeeId_fkey: foreignKey({
        columns: [table.distanceFeeId],
        foreignColumns: [distance_fee.id],
        name: 'price_distanceFeeId_fkey',
      }),
      price_fuelSurchargeId_fkey: foreignKey({
        columns: [table.fuelSurchargeId],
        foreignColumns: [fuel_surcharge.id],
        name: 'price_fuelSurchargeId_fkey',
      }),
      price_defaultBusinessLineId_business_line_config_id_fk: foreignKey({
        columns: [table.defaultBusinessLineId],
        foreignColumns: [business_line_config.id],
        name: 'price_defaultBusinessLineId_business_line_config_id_fk',
      }),
    };
  },
);

/**
 * Historical record of changes to prices.
 * Tracks all modifications to price records over time for auditing and versioning.
 * Contains the same fields as price but allows nulls for partial updates.
 */
export const price_history = pgTable(
  'price_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    billingDisabled: boolean('billingDisabled'),
    populateWhenZero: boolean('populateWhenZero'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    priceType: varchar('priceType', { length: 255 }),
    chargeFee: bigint('chargeFee', { mode: 'number' }),
    chargePercentage: decimal('chargePercentage', { precision: 7, scale: 4 }),
    skuId: uuid('skuId'),
    surchargeName: varchar('surchargeName', { length: 255 }),
    surchargeOrder: integer('surchargeOrder'),
    surchargeType: surcharge_type('surchargeType'),
    fuelSurchargeId: uuid('fuelSurchargeId'),
    recurrenceName: varchar('recurrenceName', { length: 255 }),
    recurrenceRule: varchar('recurrenceRule', { length: 255 }),
    taxable: boolean('taxable'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    distanceFeeId: uuid('distanceFeeId'),
    qbItemId: varchar('qbItemId', { length: 255 }),
    qbClassId: varchar('qbClassId', { length: 255 }),
    priceName: text('priceName'),
    inheritOrderQuantity: boolean('inheritOrderQuantity'),
    defaultQuantity: decimal('defaultQuantity', { precision: 12, scale: 4 }),
    defaultBusinessLineId: uuid('defaultBusinessLineId'),
    minimumQuantity: decimal('minimumQuantity', { precision: 100, scale: 4 }),
    minimumPriceCents: bigint('minimumPriceCents', { mode: 'number' }),
    recurringForwardOrBackward: recurring_forward_or_backward(
      'recurringForwardOrBackward',
    ),
    chargeTriggers: price_charge_trigger('chargeTriggers').array(),
    exceptionTypeIds: uuid('exceptionTypeIds').array(),
    calculationBase: price_calculation_base('calculationBase').array(),
    driverVisibility: driver_visibility('driverVisibility'),
    unitOfMeasure: price_unit_of_measure('unitOfMeasure'),
    recurringPeriodAmountCents: bigint('recurringPeriodAmountCents', {
      mode: 'number',
    }),
    canadianTaxApplicability: canadian_tax_applicability(
      'canadianTaxApplicability',
    ),
    excludeCredits: boolean('excludeCredits'),
    descriptionContent: jsonb('descriptionContent').$type<DescriptionContent>(),
  },
  (table) => {
    return {
      skuId_idx: index('price_history_skuId_idx').on(table.skuId),
      tenant_updated_idx: index('price_history_tenant_updated_idx').on(
        table.tenantId,
        table.updatedAtTs,
      ),
      id_version_idx: index('price_history_id_version_idx').on(
        table.id,
        table.versionNumber,
      ),
    };
  },
);

/** Type for driver visibility enum values */
export type DriverVisibility = (typeof driver_visibility)['enumValues'][number];

/**
 * Maps extra fees (prices) to specific drivers who can see them.
 * Only used when price.driverVisibility = 'SOME'.
 * When visibility is 'ALL' or 'NONE', no rows are created in this table.
 */
export const price_driver_visibility = pgTable(
  'price_driver_visibility',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Reference to the price/extra fee */
    priceId: uuid('priceId')
      .notNull()
      .references(() => price.id),
    /** Reference to the identity user (driver) who can see this fee */
    identityUserId: uuid('identityUserId').notNull(),
    /** UUID of the user who last modified the record */
    lastModifiedBy: uuid('lastModifiedBy'),
    /** UUID of the user who created the record */
    createdBy: uuid('createdBy'),
  },
  (table) => {
    return {
      createdAtTs_idx: index('price_driver_visibility_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      updatedAtTs_idx: index('price_driver_visibility_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      priceId_idx: index('price_driver_visibility_priceId_idx').on(
        table.priceId,
      ),
      // Unique constraint: one driver can only have one visibility entry per price
      unique_price_driver: index(
        'price_driver_visibility_unique_price_driver',
      ).on(table.tenantId, table.priceId, table.identityUserId),
    };
  },
);

/**
 * Historical record of price driver visibility mappings.
 */
export const price_driver_visibility_history = pgTable(
  'price_driver_visibility_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    priceId: uuid('priceId'),
    identityUserId: uuid('identityUserId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
  },
);
